import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import NavBar from '../Components/NavBar';

const AdminSettings = () => {
  const [user, setUser] = useState(null);
  const [adminEmails, setAdminEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only allow when 'admin' team is selected in localStorage
    const currentTeam = window.localStorage.getItem('currentTeam');
    if (currentTeam !== 'admin') {
      setIsAuthorized(false);
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        loadAdminEmails();
      } else {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const loadAdminEmails = async () => {
    setError('');
    setLoading(true);
    setIsAuthorized(true);
    try {
      const getAdmins = httpsCallable(functions, 'getAdminEmails');
      const result = await getAdmins();
      setAdminEmails(result.data.emails || []);
    } catch (err) {
      if (err.code === 'permission-denied') {
        setIsAuthorized(false);
        setError('You are not authorized to manage admin settings.');
      } else {
        setError(err.message || 'Failed to load admin emails.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail.trim()) {
      setError('Please enter an email address.');
      return;
    }

    try {
      const addAdmin = httpsCallable(functions, 'addAdminEmail');
      const result = await addAdmin({ email: newEmail.trim() });
      setAdminEmails(result.data.emails || []);
      setNewEmail('');
      setSuccess('Admin email added successfully.');
    } catch (err) {
      setError(err.message || 'Failed to add admin email.');
    }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    setError('');
    setSuccess('');

    try {
      const removeAdmin = httpsCallable(functions, 'removeAdminEmail');
      const result = await removeAdmin({ email: emailToRemove });
      setAdminEmails(result.data.emails || []);
      setSuccess('Admin email removed successfully.');
    } catch (err) {
      setError(err.message || 'Failed to remove admin email.');
    }
  };

  if (!user) {
    return (
      <>
        <NavBar pageName="Admin Settings" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Admin Settings</Card.Title>
                  <Card.Text>Please sign in to manage admin settings.</Card.Text>
                  <Button onClick={() => navigate('/admin-login')}>Sign in as Admin</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (user && !isAuthorized) {
    return (
      <>
        <NavBar pageName="Admin Settings" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Not Authorized</Card.Title>
                  <Card.Text>You are not authorized to manage admin settings.</Card.Text>
                  {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                  <Button variant="secondary" onClick={() => navigate('/review-team-requests')}>Back to Review</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavBar pageName="Admin Settings" showMenu={false} />
      <Container className="my-4">
        <Row className="mb-3">
          <Col>
            <h2>Admin Settings</h2>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row>
          <Col md={8} lg={6}>
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>Manage Admin Emails</Card.Title>

                <h5 className="mt-4 mb-3">Current Admins</h5>
                {loading ? (
                  <div className="text-muted">Loading...</div>
                ) : adminEmails.length === 0 ? (
                  <div className="text-muted">No admin emails configured.</div>
                ) : (
                  <ListGroup className="mb-4">
                    {adminEmails.map((email) => (
                      <ListGroup.Item key={email} className="d-flex justify-content-between align-items-center">
                        <span>{email}</span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveEmail(email)}
                        >
                          Remove
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}

                <h5 className="mt-4 mb-3">Add New Admin</h5>
                <Form onSubmit={handleAddEmail}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="admin@example.com"
                    />
                  </Form.Group>
                  <Button type="submit" variant="primary">Add Admin Email</Button>
                </Form>
              </Card.Body>
            </Card>

            <Button variant="secondary" onClick={() => navigate('/review-team-requests')}>Back to Review Requests</Button>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdminSettings;
